import { Fund } from 'src/components/Exchange/types';
import { ItemSelection, WithholdingObject } from 'src/types';
import { action } from 'typesafe-actions';
import { ExchangeType, UNIT } from 'src/settings/settings';
import { createApiPostActionCreator } from '../../utils/createApiPostActionCreator';
import { fundToMutualFundAgreementKey } from '../../utils/module-util';
import { ExchangeTransactionActionType as act } from './actionTypes';

export const selectExchangeType = (exchangeType: ExchangeType) => {
  return action(act.SELECT_EXCHANGE_TYPE, { exchangeType });
};

export const acceptMarketFluctuationDisclosure = (
  acceptedMarketFluctuationDisclosure
) => {
  return action(act.ACCEPT_MARKET_FLUCTUATION_DISCLOSURE, {
    acceptedMarketFluctuationDisclosure,
  });
};

export const selectMonth = (selection: ItemSelection[]) =>
  action(act.UPDATE_MONTH_SELECTION, {
    selection,
  });

export const selectDay = (selection: ItemSelection[]) =>
  action(act.UPDATE_DAY_SELECTION, {
    selection,
  });

export const selectUnit = (grossUnit: UNIT) =>
  action(act.UPDATE_UNIT_SELECTION, { grossUnit });

export const updateGrossAmount = (grossAmount: number) =>
  action(act.UPDATE_GROSS_AMOUNT, { grossAmount });

export const updateUniqueIDs = (
  electionAgreementUniqueId1: number,
  electionAgreementUniqueId2: number
) =>
  action(act.UPDATE_UNIQUE_IDS, {
    electionAgreementUniqueId1,
    electionAgreementUniqueId2,
  });

export const selectExchangeFromFund = (
  agreementID: Fund['agreementID'],
  fund: Fund
) => {
  return action(act.SELECT_EXCHANGE_FROM_FUND, {
    agreementID,
    registrationGroup: fund.registrationGroup,
    fund,
  });
};

export const searchExchangeToExisting = (fund: Fund) => {
  return createApiPostActionCreator(
    act.SEARCH_EXCHANGE_TO_EXISTING,
    'exchangeToExistingSearch',
    fundToMutualFundAgreementKey(fund)
  );
};

export const deselectExchangeFromFund = (agreementID: Fund['agreementID']) => {
  return action(act.DESELECT_EXCHANGE_FROM_FUND, { agreementID });
};

export const updateExchangeFund = (type: act, toUpdate: Partial<Fund>) => {
  const { agreementID } = toUpdate;
  if (!agreementID) {
    throw new Error(
      'Cannot update fund when no agreementID specified. ' +
        JSON.stringify(toUpdate)
    );
  }
  return action(type, toUpdate);
};

export const acceptRI2020WithholdingDisclosure = (
  acceptedRI2020WithholdingDisclosure
) => {
  return action(act.ACCEPT_RI2020_WITHHOLDING_DISCLOSURE, {
    acceptedRI2020WithholdingDisclosure,
  });
};

export const updateExchangeFromFundAmount = (
  agreementID: Fund['agreementID'],
  grossAmount: Fund['grossAmount']
) => {
  return updateExchangeFund(act.UPDATE_EXCHANGE_FROM_FUND, {
    agreementID,
    grossAmount,
  });
};

export function updateExchangeFromFundUnit(
  agreementID: Fund['agreementID'],
  grossUnit: Fund['grossUnit']
) {
  const grossAmount = undefined;
  return updateExchangeFund(act.UPDATE_EXCHANGE_FROM_FUND, {
    agreementID,
    grossUnit,
    grossAmount,
  });
}

export const selectExchangeToFund = (
  agreementID: Fund['agreementID'],
  fund: Fund
) => {
  return action(act.SELECT_EXCHANGE_TO_FUND, {
    agreementID,
    registrationGroup: fund?.registrationGroup,
    fund,
  });
};

export const deselectExchangeToFund = (agreementID: Fund['agreementID']) => {
  return action(act.DESELECT_EXCHANGE_TO_FUND, { agreementID });
};

export const updateExchangeToFundAmount = (
  agreementID: Fund['agreementID'],
  grossAmount
) => {
  return updateExchangeFund(act.UPDATE_EXCHANGE_TO_FUND, {
    agreementID,
    grossAmount,
  });
};

export const updateExchangeToFundUnit = (agreementID, grossUnit) => {
  return updateExchangeFund(act.UPDATE_EXCHANGE_TO_FUND, {
    agreementID,
    grossUnit,
  });
};

export const updateExchangeToFundContributionYear = (
  agreementID,
  contributionYear
) => {
  return updateExchangeFund(act.UPDATE_EXCHANGE_TO_FUND, {
    agreementID,
    contributionYear,
  });
};

export const addExchangeToNewFund = (fund: Fund) => {
  return action(act.ADD_NEW_EXCHANGE_TO_FUND, { fund });
};

export const updateExchangeContributionYear = (
  exchangeContributionYear: string
) =>
  action(act.UPDATE_EXCHANGE_CONTRIBUTION_YEAR, {
    exchangeContributionYear,
  });

export const updateExchangeFederalWithholding = (
  exchangeFederalWithholdingApplied: boolean,
  exchangeFederalWithholdingPercent: number
) => {
  return action(act.UPDATE_EXCHANGE_FEDERAL_WITHHOLDING, {
    exchangeFederalWithholdingApplied,
    exchangeFederalWithholdingPercent,
  });
};

export const updateExchangeFederalWithholdingWithObject = ({
  federalWithholdingApplied,
  federalWithholdingPercent,
  agreementID,
}: WithholdingObject) =>
  action(
    act.UPDATE_EXCHANGE_FEDERAL_WITHHOLDING,
    {
      federalWithholdingApplied,
      federalWithholdingPercent,
    },
    { agreementID }
  );

export const swapExchangeToFund = (agreementID, fund: Fund) => {
  return action(act.SWAP_NEW_EXCHANGE_TO_FUND, { agreementID, fund });
};
